import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Package, Receipt, Store } from "lucide-react";
import {
  BONUS_TYPE_LABELS,
  NETWORK_ROUTES,
  formatPrice,
  memberActivityResponseSchema,
  type BonusType,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { Badge } from "@/shared/ui/core";
import { formatOrderDate } from "@/features/admin/orders/format";

/**
 * ═══════════════════════════════════════════════════════════════════
 * QUÉ COMPRÓ alguien de mi red, y qué me generó a mí.
 *
 * (Anahí, 29-ago): "deben poder ver las comisiones y puntos que ha
 * generado cada una de las personas que han invitado [...] producto o
 * paquete, monto, puntos y comisión, y el tipo de comisión — si es BIR,
 * si es BUR."
 *
 * ── Solo LO MÍO ──────────────────────────────────────────────────────
 * El servidor devuelve, de cada compra, lo que cobró quien pregunta —
 * nunca lo que cobraron los otros cuatro niveles de la línea. Enseñar el
 * reparto completo sería contarle a cada quien lo que ganan sus
 * compañeros.
 *
 * ── Un cero con MOTIVO ───────────────────────────────────────────────
 * Una compra puede no haber pagado nada: el bono de inicio solo cubre la
 * primera, o el nivel quedó fuera del gradiente. Se enseña igual, con la
 * comisión en cero y sin bono. Esconderla haría pensar que la compra no
 * existió, y "¿de esta no gané?" es justo la pregunta que esta tabla
 * tiene que responder.
 * ═══════════════════════════════════════════════════════════════════
 */
const ICONO = {
  producto: Receipt,
  paquete: Package,
  franquicia: Store,
} as const;

export function MemberActivity({ userId }: { userId: string }) {
  const { t } = useTranslation();

  const actividad = useQuery({
    queryKey: ["network", "member-activity", userId],
    queryFn: () =>
      authFetch(NETWORK_ROUTES.memberActivity(userId), memberActivityResponseSchema, {
        baseUrl: "",
      }),
  });

  if (actividad.isPending) {
    return <p className="px-3 py-2 text-[12.5px] text-ds-ink-3">{t("Cargando su actividad…")}</p>;
  }

  const data = actividad.data;
  if (!data || data.purchases.length === 0) {
    return (
      <p className="px-3 py-2 text-[12.5px] text-ds-ink-3">
        {t("Todavía no ha comprado nada. Cuando lo haga, aquí verás qué te genera.")}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-ds-border bg-white">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-ds-border bg-ds-cream-2/40">
            {[
              t("Qué compró"),
              t("Monto"),
              t("Puntos"),
              t("Bono"),
              t("Tu comisión"),
              t("Fecha"),
            ].map((label, index) => (
              <th
                key={label}
                className={`px-3 py-2 text-[11px] font-bold uppercase tracking-ds-label text-ds-ink-3 ${
                  index >= 1 && index <= 2 ? "text-right" : index === 4 ? "text-right" : ""
                }`}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.purchases.map((compra) => {
            const Icono = ICONO[compra.kind];
            return (
              <tr key={compra.orderId} className="border-b border-ds-border/60 last:border-0">
                <td className="px-3 py-2.5">
                  <span className="flex items-start gap-2">
                    <Icono className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ds-ink-3" />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium text-ds-ink">
                        {compra.concept}
                      </span>
                      <span className="font-mono text-[11px] text-ds-ink-3">{compra.folio}</span>
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[13px]">
                  {formatPrice(compra.amountCents, "MXN")}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[13px] text-ds-ink-3">
                  {compra.volumePoints}
                </td>
                <td className="px-3 py-2.5">
                  {compra.bonusType ? (
                    <Badge variant="info" size="sm">
                      {compra.bonusType}
                      {compra.level ? ` · N${compra.level}` : ""}
                    </Badge>
                  ) : (
                    <span className="text-[12px] text-ds-ink-3">{t("No generó")}</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <span
                    className={
                      compra.commissionCents > 0
                        ? "font-mono text-[13px] font-semibold text-ds-ink"
                        : "font-mono text-[13px] text-ds-ink-3"
                    }
                  >
                    {formatPrice(compra.commissionCents, "MXN")}
                  </span>
                  {compra.bonusType && (
                    <span className="block text-[11px] text-ds-ink-3">
                      {t(BONUS_TYPE_LABELS[compra.bonusType as BonusType] ?? compra.bonusType)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[12px] text-ds-ink-3">
                  {formatOrderDate(compra.paidAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-ds-mist/40 text-[13px] font-semibold text-ds-ink">
            <td className="px-3 py-2.5">{t("Total")}</td>
            <td className="px-3 py-2.5 text-right font-mono">
              {formatPrice(data.totals.amountCents, "MXN")}
            </td>
            <td className="px-3 py-2.5 text-right font-mono">{data.totals.volumePoints}</td>
            <td />
            <td className="px-3 py-2.5 text-right font-mono">
              {formatPrice(data.totals.commissionCents, "MXN")}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
